import { translate as t } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { useCallback, useState } from 'react';
import { create } from '../../../utils/logger';
import { validateFile } from './csv-validation.util';
const logger = create('Admin.ManualTranslation.Upload');
export function useTranslationUpload(isAllOrgContext = false) {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const orgScopeParam = isAllOrgContext ? '?org_scope=ALL' : '';
    const uploadCSV = useCallback(async (file) => {
        const validation = validateFile(file);
        if (!validation.isValid) {
            setUploadError(validation.error ||
                t('adminSection.manualTranslation.uploadFailed'));
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_TRANSLATION_CSV_UPLOAD_ERROR), { errorType: 'validation' });
            return { success: false, error: validation.error };
        }
        setUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('filename', file.name);
            const response = await fetch(`/prism/v1/localization/upload${orgScopeParam}`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });
            if (!response.ok) {
                let detail;
                try {
                    const errorData = await response.json();
                    detail = errorData?.error || errorData?.message;
                    logger.warn('Manual translation upload rejected by server', detail ?? `HTTP ${response.status}`);
                }
                catch {
                    logger.warn('Manual translation upload failed', `HTTP ${response.status}`);
                }
                collectEvent(new UserAction(userActionTypeEnum.MANUAL_TRANSLATION_CSV_UPLOAD_ERROR), {
                    errorStatus: response.status,
                    errorType: 'http-error',
                });
                const userError = detail ||
                    t('adminSection.manualTranslation.uploadFailed');
                setUploadError(userError);
                return { success: false, error: userError };
            }
            let result;
            try {
                result = await response.json();
            }
            catch (parseError) {
                logger.warn('Manual translation upload: failed to parse response', parseError);
                const userError = t('adminSection.manualTranslation.uploadFailed');
                setUploadError(userError);
                collectEvent(new UserAction(userActionTypeEnum.MANUAL_TRANSLATION_CSV_UPLOAD_ERROR), { errorType: 'invalid-response' });
                return { success: false, error: userError };
            }
            if (result.success === false) {
                logger.warn('Manual translation upload rejected', result.error);
                const userError = result.error ||
                    t('adminSection.manualTranslation.uploadFailed');
                setUploadError(userError);
                collectEvent(new UserAction(userActionTypeEnum.MANUAL_TRANSLATION_CSV_UPLOAD_ERROR), { errorType: 'rejected' });
                return { success: false, error: userError };
            }
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_TRANSLATION_CSV_UPLOAD_SUCCESS));
            setUploadError(null);
            return { success: true };
        }
        catch (error) {
            logger.warn('Manual translation upload error', error);
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_TRANSLATION_CSV_UPLOAD_ERROR), { errorType: 'exception' });
            const userError = t('adminSection.manualTranslation.uploadFailed');
            setUploadError(userError);
            return { success: false, error: userError };
        }
        finally {
            setUploading(false);
        }
    }, [orgScopeParam]);
    const clearUploadError = useCallback(() => {
        setUploadError(null);
    }, []);
    const [downloadingExistingCsv, setDownloadingExistingCsv] = useState(false);
    const [existingCsvDownloadError, setExistingCsvDownloadError] = useState(null);
    const checkCsvExists = useCallback(async () => {
        try {
            const response = await fetch(`/prism/v1/localization/csv-status${orgScopeParam}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                return false;
            }
            const data = await response.json();
            return Boolean(data?.exists);
        }
        catch {
            return false;
        }
    }, [orgScopeParam]);
    const downloadExistingCsv = useCallback(async () => {
        setDownloadingExistingCsv(true);
        setExistingCsvDownloadError(null);
        try {
            const response = await fetch(`/prism/v1/localization/download-csv${orgScopeParam}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'manual-translation.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE_DOWNLOAD_EXISTING_CSV));
        }
        catch (error) {
            logger.warn('Download existing CSV failed', error);
            setExistingCsvDownloadError(t('adminSection.manualTranslation.downloadCsvError'));
        }
        finally {
            setDownloadingExistingCsv(false);
        }
    }, [orgScopeParam]);
    const clearExistingCsvDownloadError = useCallback(() => {
        setExistingCsvDownloadError(null);
    }, []);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const deleteCSV = useCallback(async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            const response = await fetch(`/prism/v1/localization/delete-csv${orgScopeParam}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                logger.warn('Manual translation delete failed', `HTTP ${response.status}`);
                collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE_DELETE_EXISTING_CSV), { errorStatus: response.status, success: false });
                const userError = t('adminSection.manualTranslation.deleteCsvError');
                setDeleteError(userError);
                return { success: false, error: userError };
            }
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE_DELETE_EXISTING_CSV), { success: true });
            return { success: true };
        }
        catch (error) {
            logger.warn('Manual translation delete error', error);
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE_DELETE_EXISTING_CSV), { errorType: 'exception', success: false });
            const userError = t('adminSection.manualTranslation.deleteCsvError');
            setDeleteError(userError);
            return { success: false, error: userError };
        }
        finally {
            setDeleting(false);
        }
    }, [orgScopeParam]);
    const clearDeleteError = useCallback(() => {
        setDeleteError(null);
    }, []);
    return {
        uploadCSV,
        uploading,
        uploadError,
        clearUploadError,
        setUploadError,
        checkCsvExists,
        downloadExistingCsv,
        downloadingExistingCsv,
        existingCsvDownloadError,
        clearExistingCsvDownloadError,
        deleteCSV,
        deleting,
        deleteError,
        clearDeleteError,
    };
}
//# sourceMappingURL=translation-upload.hook.js.map